const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const WEEKDAY_ORDER = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
};

export function todayRomeDateString(date = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

export function listUpcomingMonths(count = 6, date = new Date()) {
    const iso = todayRomeDateString(date);
    const { year, month } = parseIsoDate(iso);
    const months = [];
    for (let i = 0; i < count; i += 1) {
        const cursor = new Date(year, month - 1 + i, 1);
        months.push(
            `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
        );
    }
    return months;
}

export function parseIsoDate(iso) {
    const [year, month, day] = String(iso).slice(0, 10).split('-').map(Number);
    return { year, month, day, date: new Date(year, month - 1, day) };
}

export function formatIsoDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function addDaysIso(iso, days) {
    const { date } = parseIsoDate(iso);
    date.setDate(date.getDate() + days);
    return formatIsoDate(date);
}

export function addMonthsIso(iso, months) {
    const { year, month, day } = parseIsoDate(iso);
    const cursor = new Date(year, month - 1 + months, 1);
    const lastDay = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    cursor.setDate(Math.min(day, lastDay));
    return formatIsoDate(cursor);
}

export function rangeForPreset(preset, today) {
    if (preset === '4w') {
        return { from: today, to: addDaysIso(today, 27) };
    }
    if (preset === '3m') {
        return { from: today, to: addMonthsIso(today, 3) };
    }
    return { from: today, to: addMonthsIso(today, 6) };
}

export function sessionDateIso(session) {
    return String(session?.session_date || '').slice(0, 10);
}

export function formatClock(time) {
    return time ? String(time).slice(0, 5) : '';
}

export function weekdayKeyFromIso(iso) {
    const { date } = parseIsoDate(iso);
    return WEEKDAY_KEYS[date.getDay()];
}

export function sessionHasCap(session) {
    const max = session?.max_participants;
    return max != null && max !== '' && Number(max) > 0;
}

export function remainingSessionSpots(session) {
    if (!sessionHasCap(session)) return Number.POSITIVE_INFINITY;
    const offers = Number(session.active_offers || 0);
    const taken = Number(session.current_participants || 0) + offers;
    return Math.max(0, Number(session.max_participants) - taken);
}

export function isSessionFull(session) {
    if (!sessionHasCap(session)) return false;
    return remainingSessionSpots(session) <= 0;
}

export function isCancelledSession(session) {
    return String(session?.status || '').toLowerCase() === 'cancelled';
}

export function sessionInRange(session, from, to) {
    const date = sessionDateIso(session);
    if (!date) return false;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
}

export function idKey(id) {
    return String(id);
}

export function buildCourseSlots(sessions) {
    const map = new Map();
    (sessions || []).forEach((session) => {
        const date = sessionDateIso(session);
        if (!date) return;
        const weekdayKey = weekdayKeyFromIso(date);
        const startTime = formatClock(session.start_time);
        const endTime = formatClock(session.end_time);
        const key = `${weekdayKey}|${startTime}|${endTime}`;
        if (!map.has(key)) {
            map.set(key, {
                key,
                weekdayKey,
                startTime,
                endTime,
                sessions: [],
            });
        }
        map.get(key).sessions.push(session);
    });

    return [...map.values()]
        .map((slot) => ({
            ...slot,
            sessions: [...slot.sessions].sort((a, b) =>
                sessionDateIso(a).localeCompare(sessionDateIso(b))
            ),
        }))
        .sort((a, b) => {
            const day = WEEKDAY_ORDER[a.weekdayKey] - WEEKDAY_ORDER[b.weekdayKey];
            if (day !== 0) return day;
            return a.startTime.localeCompare(b.startTime);
        });
}

export function openSessionsInRange(sessions, { from, to, isBlocked } = {}) {
    const blocked = isBlocked || (() => false);
    return (sessions || []).filter(
        (session) => sessionInRange(session, from, to) && !blocked(session)
    );
}

export function slotSelectionState(slotSessions, selectedIds, options) {
    const open = openSessionsInRange(slotSessions, options);
    if (open.length === 0) return 'empty';
    const selected = new Set((selectedIds || []).map(idKey));
    const picked = open.filter((session) => selected.has(idKey(session.id))).length;
    if (picked === 0) return 'none';
    if (picked === open.length) return 'all';
    return 'partial';
}

export function toggleSlotSelection(selectedIds, slotSessions, options) {
    const targets = openSessionsInRange(slotSessions, options);
    if (targets.length === 0) return selectedIds || [];
    const current = selectedIds || [];
    const selected = new Set(current.map(idKey));
    const allOn = targets.every((session) => selected.has(idKey(session.id)));
    if (allOn) {
        const remove = new Set(targets.map((session) => idKey(session.id)));
        return current.filter((id) => !remove.has(idKey(id)));
    }
    const next = [...current];
    targets.forEach((session) => {
        if (!selected.has(idKey(session.id))) {
            next.push(session.id);
            selected.add(idKey(session.id));
        }
    });
    return next;
}

export function toggleAllSlots(selectedIds, sessions, options) {
    return toggleSlotSelection(selectedIds, sessions, options);
}

export function selectionOutsideRange(sessions, selectedIds, from, to) {
    const selected = new Set((selectedIds || []).map(idKey));
    return (sessions || []).filter(
        (session) => selected.has(idKey(session.id)) && !sessionInRange(session, from, to)
    );
}

export function dropOutsideRange(selectedIds, sessions, from, to) {
    const outside = new Set(
        selectionOutsideRange(sessions, selectedIds, from, to).map((session) => idKey(session.id))
    );
    return (selectedIds || []).filter((id) => !outside.has(idKey(id)));
}

export function groupSessionsByMonth(sessions) {
    const groups = [];
    const map = new Map();
    (sessions || []).forEach((session) => {
        const key = sessionDateIso(session).slice(0, 7);
        if (!key) return;
        if (!map.has(key)) {
            const group = { key, sessions: [] };
            map.set(key, group);
            groups.push(group);
        }
        map.get(key).sessions.push(session);
    });
    return groups;
}
